import { motion, useInView } from "framer-motion";
import { useRef } from "react";

/**
 * Portfolio Health — animated radial score infographic.
 * Pure SVG, design-token colors. No stock icons.
 */
const PortfolioHealthPreview = () => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  // Score ring math
  const size = 280;
  const stroke = 22;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const score = 86; // 0..100
  const dash = (score / 100) * c;

  // Sub-scores shown as horizontal bars
  const bars = [
    { label: "Diversification", value: 92, color: "hsl(var(--primary))" },
    { label: "Risk balance", value: 78, color: "hsl(var(--brand-amber))" },
    { label: "Income stability", value: 84, color: "hsl(var(--accent))" },
  ];

  return (
    <motion.div
      ref={ref}
      className="relative w-full max-w-[440px] mx-auto"
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="relative rounded-[2rem] bg-card border border-border/60 shadow-2xl p-8 overflow-hidden">
        {/* Soft background wash */}
        <div
          className="absolute inset-0 opacity-60 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at top, hsl(var(--primary) / 0.08), transparent 60%)",
          }}
        />

        {/* Score ring */}
        <div className="relative flex items-center justify-center mb-8">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
            {/* track */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke="hsl(var(--muted))"
              strokeWidth={stroke}
              strokeLinecap="round"
              opacity={0.35}
            />
            {/* progress */}
            <motion.circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke="url(#health-grad)"
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={c}
              initial={{ strokeDashoffset: c }}
              animate={inView ? { strokeDashoffset: c - dash } : {}}
              transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            />
            <defs>
              <linearGradient id="health-grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="hsl(var(--primary))" />
                <stop offset="100%" stopColor="hsl(var(--accent))" />
              </linearGradient>
            </defs>

            {/* tick marks */}
            {Array.from({ length: 36 }).map((_, i) => {
              const angle = (i / 36) * 2 * Math.PI;
              const x1 = size / 2 + Math.cos(angle) * (r - stroke / 2 - 14);
              const y1 = size / 2 + Math.sin(angle) * (r - stroke / 2 - 14);
              const x2 = size / 2 + Math.cos(angle) * (r - stroke / 2 - 6);
              const y2 = size / 2 + Math.sin(angle) * (r - stroke / 2 - 6);
              return (
                <motion.line
                  key={i}
                  x1={x1} y1={y1} x2={x2} y2={y2}
                  stroke="hsl(var(--foreground))"
                  strokeWidth={1}
                  opacity={0.18}
                  initial={{ opacity: 0 }}
                  animate={inView ? { opacity: 0.18 } : {}}
                  transition={{ delay: 0.4 + i * 0.012 }}
                />
              );
            })}
          </svg>

          {/* Center label */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <motion.span
              className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground"
              initial={{ opacity: 0, y: 6 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.8 }}
            >
              Health Score
            </motion.span>
            <motion.div
              className="flex items-baseline gap-1 mt-1"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.9, duration: 0.5 }}
            >
              <CountUp to={score} inView={inView} className="text-6xl font-extrabold text-foreground tabular-nums" />
              <span className="text-2xl font-bold text-muted-foreground">/100</span>
            </motion.div>
            <motion.span
              className="mt-2 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase"
              style={{
                background: "hsl(var(--primary) / 0.12)",
                color: "hsl(var(--primary))",
              }}
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 1.1 }}
            >
              Strong
            </motion.span>
          </div>
        </div>

        {/* Sub-score bars */}
        <div className="space-y-4 relative">
          {bars.map((b, i) => (
            <div key={b.label}>
              <div className="flex items-baseline justify-between mb-1.5">
                <span className="text-[12px] font-semibold text-foreground/85">{b.label}</span>
                <motion.span
                  className="text-[12px] font-bold tabular-nums"
                  style={{ color: b.color }}
                  initial={{ opacity: 0 }}
                  animate={inView ? { opacity: 1 } : {}}
                  transition={{ delay: 1.0 + i * 0.15 }}
                >
                  {b.value}
                </motion.span>
              </div>
              <div className="h-1.5 rounded-full bg-muted/60 overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: b.color }}
                  initial={{ width: 0 }}
                  animate={inView ? { width: `${b.value}%` } : {}}
                  transition={{ delay: 0.6 + i * 0.15, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-2/3 h-20 bg-primary/30 blur-3xl rounded-full -z-10" />
    </motion.div>
  );
};

const CountUp = ({ to, inView, className }: { to: number; inView: boolean; className?: string }) => {
  const ref = useRef<HTMLSpanElement>(null);
  // Lightweight count-up driven by framer-motion via CSS only would need a hook.
  // Use a simple animated number via requestAnimationFrame.
  if (typeof window !== "undefined" && inView && ref.current) {
    const el = ref.current;
    const start = performance.now();
    const dur = 1400;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = Math.round(eased * to).toString();
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }
  return <span ref={ref} className={className}>0</span>;
};

export default PortfolioHealthPreview;
